/**
 * @module useChart
 * Sales chart composable built on Apache ECharts.
 *
 * Wraps `useFilter` to inherit all filter state and sales-DB lookup functions,
 * then adds chart-specific data fetching, transformations, and ECharts option
 * builders.
 *
 * Two chart modes:
 * - "sales"    — raw sales amounts per time unit
 * - "averages" — daily/monthly averages (excludes the current day/month since
 *                data entry for today may still be in progress)
 *
 * The API returns a deeply nested object keyed as:
 *   data[city][location][brand][platform][timeUnit] = amount
 *
 * `filteredData` traverses this tree based on the active filter selection,
 * summing across all unfiltered dimensions.
 */

import * as echarts from "echarts";
import { ref, reactive, computed, watch } from "vue";
import api from "@/utils/api.js";
import { useFilter } from "./useFilter.js";

export function useChart() {
  // Import ALL filter functionality — useChart is a superset of useFilter
  const {
    // Filter state
    filters,
    filterApplied,
    filterCleared,
    isFilterExpanded,
    activeFilters,
    hasActiveFilters,
    filterTotals,
    totalsSummary,

    // Filter options
    cityOptions,
    locationOptions,
    brandOptions,
    options,
    datePresets,
    dateRangePreset,

    // Filter actions
    fetchOptions,
    applyFilters,
    clearFilters,
    setFilter,
    setFilters,
    applyDatePreset,
    shallowEqual,
    deepEqual,

    // Table data functions
    getCitiesWithData,
    getLocationsWithData,
    getBrandsWithData,
    getPlatformsWithData,
    getLocationCityTotal,
    getBrandLocationCityTotal,
    getPlatformTotalForBrand,
    getLocationCityDayTotal,
    getBrandLocationCityDayTotal,
    getPlatformData,
    getCityTotal,
    getLocationTotal,
    getBrandTotal,
    getCityAverage,
    getLocationCityAverage,
    getBrandLocationCityAverage,
    getPlatformAverageForBrand,
    getGrandAverage,

    // Expanded states
    expandedCities,
    expandedLocations,
    expandedBrands,
    isCityExpanded,
    isLocationExpanded,
    isBrandExpanded,
    toggleCity,
    toggleLocation,
    toggleBrand,
    expandAll,
    collapseAll,

    // Computed properties
    filterSummary,
    numberOfActiveFilters,

    // Actions
    toggleFilterExpand,
    formatCurrency: filterFormatCurrency,
    formatAmount: filterFormatAmount,
  } = useFilter();

  // ── Chart global state ───────────────────────────────────────────────────────

  const showChartMenu = ref(false);
  const chartMode = ref("sales"); // "sales" or "averages"
  const chartData = ref(null);
  const chartLoading = ref(false);
  const chartError = ref("");
  const chartExporting = ref(false);
  const chartType = ref("bar");
  const isDailyChart = ref(false); // false = monthly, true = daily

  // Dark theme palette — kept here (not in a CSS file) because ECharts options
  // are plain objects and don't participate in Vuetify's theming system
  const darkTheme = {
    textColor: "#ffffff",
    axisLineColor: "#666666",
    splitLineColor: "#333333",
    tooltipBackground: "#2a2a2a",
    tooltipBorder: "#444444",
    seriesColor: "#4fc3f7",
    areaGradient: {
      start: "rgba(79, 195, 247, 0.3)",
      end: "rgba(79, 195, 247, 0.1)",
    },
  };

  // ── Utility functions ────────────────────────────────────────────────────────

  const safeArray = (arr) => {
    return Array.isArray(arr) ? arr : [];
  };

  /** Safely traverse a nested object by an array of keys without throwing. */
  const getSafeValue = (obj, path, defaultValue = 0) => {
    if (!obj) return defaultValue;
    return path.reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : defaultValue;
    }, obj);
  };

  const formatMonthForDisplay = (monthString) => {
    const [year, month] = monthString.split("-");
    const date = new Date(year, month - 1);
    return date.toLocaleDateString("en-US", { year: "numeric", month: "short" });
  };

  // Compact axis label: "7/01" — short enough for 31 daily bars without overlap
  const formatDayForAxis = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    return `${day}/${month}`;
  };

  // Verbose tooltip label: "7 Jan (Mon)"
  const formatDayForDisplay = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleDateString("en-US", { month: "short" });
    const weekday = date.toLocaleDateString("en-US", { weekday: "short" });
    return `${day} ${month} (${weekday})`;
  };

  /** Detect format by string length and delegate to the appropriate formatter. */
  const formatTimeUnitForDisplay = (timeUnit) => {
    if (!timeUnit) return "";
    if (timeUnit.includes("-")) {
      if (timeUnit.length === 10) {
        return formatDayForDisplay(timeUnit);   // YYYY-MM-DD
      } else if (timeUnit.length === 7) {
        return formatMonthForDisplay(timeUnit); // YYYY-MM
      }
    }
    return timeUnit;
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // ── Derived chart data ───────────────────────────────────────────────────────

  const hasData = computed(() => {
    if (!chartData.value) {
      return false;
    }

    if (isDailyChart.value) {
      const days = chartData.value.days;
      return Array.isArray(days) && days.length > 0;
    } else {
      const months = chartData.value.months;
      const hasMonthsData = Array.isArray(months) && months.length > 0;
      const hasGrandTotal = chartData.value.grandTotal > 0;

      if (chartData.value.data && typeof chartData.value.data === "object") {
        const cities = Object.keys(chartData.value.data);
        if (cities.length > 0) {
          return true;
        }
      }

      return hasMonthsData || hasGrandTotal;
    }
  });

  /** Extract the ordered list of time units from the API response. */
  const timeUnits = computed(() => {
    if (!chartData.value) {
      return [];
    }

    if (isDailyChart.value) {
      const days = chartData.value.days || [];
      return Array.isArray(days) ? days : [];
    } else {
      const months = chartData.value.months || [];

      if (months.length === 0 && chartData.value.data) {
        // Fallback: derive months by walking the data tree when the `months`
        // array is missing (older API responses)
        const allMonths = new Set();
        Object.values(chartData.value.data).forEach((cityData) => {
          Object.values(cityData).forEach((locationData) => {
            Object.values(locationData).forEach((brandData) => {
              Object.values(brandData).forEach((platformData) => {
                Object.keys(platformData).forEach((key) => {
                  if (/^\d{4}-\d{2}$/.test(key)) {
                    allMonths.add(key);
                  }
                });
              });
            });
          });
        });

        const extractedMonths = Array.from(allMonths).sort();
        return extractedMonths;
      }

      return Array.isArray(months) ? months : [];
    }
  });

  /**
   * Apply the active filter selection to collapse the 5-dimensional data tree
   * into a flat array of amounts indexed by time unit.
   *
   * Traversal strategy: for each unfiltered dimension, iterate all known values
   * from the API's lookup arrays (cities, locations, brands, platforms) and sum.
   * This is O(n) across all combinations for each time unit.
   */
  const filteredData = computed(() => {
    if (!chartData.value) return null;

    const units = timeUnits.value;
    const data = [];

    const currentCity = filters.city;
    const currentLocation = filters.location;
    const currentBrand = filters.brand;
    const currentPlatform = filters.platform;

    units.forEach((unit) => {
      let unitTotal = 0;

      if (currentCity && currentLocation && currentBrand && currentPlatform) {
        // All dimensions pinned — direct lookup
        unitTotal += getSafeValue(
          chartData.value.data,
          [currentCity, currentLocation, currentBrand, currentPlatform, unit],
          0,
        );
      } else if (currentCity && currentLocation && currentBrand) {
        const platforms = safeArray(chartData.value.platforms);
        platforms.forEach((platform) => {
          unitTotal += getSafeValue(
            chartData.value.data,
            [currentCity, currentLocation, currentBrand, platform, unit],
            0,
          );
        });
      } else if (currentCity && currentLocation) {
        const brands = safeArray(chartData.value.brands);
        brands.forEach((brand) => {
          const platforms = safeArray(chartData.value.platforms);
          platforms.forEach((platform) => {
            unitTotal += getSafeValue(
              chartData.value.data,
              [currentCity, currentLocation, brand, platform, unit],
              0,
            );
          });
        });
      } else if (currentCity) {
        const locations = safeArray(chartData.value.locations);
        locations.forEach((location) => {
          const brands = safeArray(chartData.value.brands);
          brands.forEach((brand) => {
            const platforms = safeArray(chartData.value.platforms);
            platforms.forEach((platform) => {
              unitTotal += getSafeValue(
                chartData.value.data,
                [currentCity, location, brand, platform, unit],
                0,
              );
            });
          });
        });
      } else {
        // No filters — use precomputed column totals for performance
        unitTotal = getGrandUnitTotal(unit);
      }

      data.push(unitTotal);
    });

    return {
      timeUnits: units,
      data: data,
      isDaily: isDailyChart.value,
      filters: {
        city: currentCity,
        location: currentLocation,
        brand: currentBrand,
        platform: currentPlatform,
      },
    };
  });

  // ── Summary statistics ───────────────────────────────────────────────────────

  const totalSales = computed(() => {
    if (!filteredData.value) return 0;
    const sum = filteredData.value.data.reduce((sum, amount) => sum + parseFloat(amount || 0), 0);
    return parseFloat(sum.toFixed(2));
  });

  const averagePerTimeUnit = computed(() => {
    if (!filteredData.value || filteredData.value.data.length === 0) return 0;
    const avg = totalSales.value / filteredData.value.data.length;
    return parseFloat(avg.toFixed(2));
  });

  const bestTimeUnit = computed(() => {
    if (!filteredData.value || filteredData.value.data.length === 0) {
      return { label: "", amount: 0 };
    }

    let maxAmount = 0;
    let bestIndex = 0;

    filteredData.value.data.forEach((amount, index) => {
      const numAmount = parseFloat(amount || 0);
      if (numAmount > maxAmount) {
        maxAmount = numAmount;
        bestIndex = index;
      }
    });

    return {
      label: formatTimeUnitForDisplay(filteredData.value.timeUnits[bestIndex]),
      amount: maxAmount,
    };
  });

  /** First-to-last growth rate — negative when the trend is downward. */
  const growthRate = computed(() => {
    if (!filteredData.value || filteredData.value.data.length < 2) return 0;

    const firstMonth = parseFloat(filteredData.value.data[0] || 0);
    const lastMonth = parseFloat(filteredData.value.data[filteredData.value.data.length - 1] || 0);

    if (firstMonth === 0) return 100;

    const growth = ((lastMonth - firstMonth) / firstMonth) * 100;
    return Number(growth.toFixed(1));
  });

  // ── Data access ──────────────────────────────────────────────────────────────

  /**
   * Sum a time unit across all brands using the precomputed `columnTotals` from
   * the API. Using columnTotals avoids re-traversing the full data tree for the
   * no-filter case, which is the most common scenario.
   */
  const getGrandUnitTotal = (unit) => {
    if (!chartData.value?.columnTotals) return 0;
    let total = 0;
    safeArray(chartData.value.brands).forEach((brand) => {
      total += parseFloat(getSafeValue(chartData.value.columnTotals, [brand, unit], 0) || 0);
    });
    return total;
  };

  // ── Data fetching ────────────────────────────────────────────────────────────

  const fetchAveragesChartData = async (customFilters = null, daily = false) => {
    chartLoading.value = true;
    chartError.value = "";

    try {
      const endpoint = daily ? "/api/accounting/sales/charts/daily-averages" : "/api/accounting/sales/charts/monthly-averages";

      const response = await api.accountingApi.get(endpoint, { params: activeFilters.value });

      if (response.data.data) {
        if (response.data.data.data) {
          console.log("Data object keys:", Object.keys(response.data.data.data));

          const firstCity = Object.keys(response.data.data.data)[0];
          if (firstCity) {
            console.log(`First city (${firstCity}) data:`, response.data.data.data[firstCity]);
          }
        }

        if (response.data.data.rowAverages) {
          console.log("Row averages keys:", Object.keys(response.data.data.rowAverages));
          const firstCityAvg = Object.keys(response.data.data.rowAverages)[0];
          if (firstCityAvg) {
            console.log(
              `First city averages (${firstCityAvg}):`,
              response.data.data.rowAverages[firstCityAvg],
            );
          }
        }

        // JSON.parse(JSON.stringify(...)) strips Vue's reactive proxy wrappers
        // from the deeply nested API response so ECharts can process it cleanly
        const cleanData = JSON.parse(JSON.stringify(response.data.data));
        chartData.value = cleanData;

        // Averages mode doesn't expose per-day order counts — set orders to 0
        filterTotals.value = {
          amount: response.data.totals?.overall_average || 0,
          amount_avg: response.data.totals?.overall_average || 0,
          number_of_days: response.data.totals?.number_of_months || 0,
          records: response.data.totals?.total_records || 0,
          orders: 0,
          excludedOrders: 0,
        };
      }
    } catch (err) {
      console.error("Failed to load averages chart data:", err);
      chartError.value = err.response?.data?.error || "Failed to load averages chart data";
    } finally {
      chartLoading.value = false;
    }
  };

  const fetchSalesChartData = async (customFilters = null, daily = false) => {
    chartLoading.value = true;
    chartError.value = "";

    try {
      const endpoint = daily ? "/api/accounting/sales/charts/daily" : "/api/accounting/sales/charts/monthly";
      console.log(`Fetching sales from ${endpoint} with filters:`, activeFilters.value);

      const response = await api.accountingApi.get(endpoint, { params: activeFilters.value });

      console.log(`=== ${daily ? "DAILY" : "MONTHLY"} SALES API RESPONSE ===`);

      if (response.data.data) {
        console.log("Sales data structure:", response.data.data);

        const cleanData = JSON.parse(JSON.stringify(response.data.data));
        chartData.value = cleanData;
        filterTotals.value = response.data.totals;
      }
    } catch (err) {
      console.error("Failed to load sales chart data:", err);
      chartError.value = err.response?.data?.error || "Failed to load sales chart data";
    } finally {
      chartLoading.value = false;
    }
  };

  /** Dispatch to the correct fetch function based on the current chart mode. */
  const fetchChartData = async (customFilters = null, daily = false) => {
    if (chartMode.value === "averages") {
      return fetchAveragesChartData(customFilters, daily);
    } else {
      return fetchSalesChartData(customFilters, daily);
    }
  };

  const setChartMode = (mode) => {
    if (mode === "sales" || mode === "averages") {
      chartMode.value = mode;
      chartData.value = null; // Clear stale data so the chart doesn't flash old values
    }
  };

  const refreshChartData = () => {
    fetchChartData(null, isDailyChart.value);
  };

  const toggleChartType = () => {
    chartType.value = chartType.value === "bar" ? "line" : "bar";
  };

  const setChartTimeframe = (daily) => {
    isDailyChart.value = daily;
    fetchChartData(null, daily);
  };

  const resetState = () => {
    chartData.value = null;
    chartLoading.value = false;
    chartError.value = "";
    clearFilters();
  };

  // ── ECharts option builders ──────────────────────────────────────────────────

  /**
   * Build the full ECharts options object for the current state.
   *
   * Label visibility: labels are hidden when there are 32+ bars because at that
   * density they overlap and become unreadable. The 32-bar threshold corresponds
   * roughly to a full month of daily data.
   *
   * Mark line: an orange dashed average line is always shown, helping the user
   * quickly see which periods are above/below the period average.
   */
  const getChartOptions = (customTheme, filteredDataParam, isDailyParam) => {
    if (!filteredDataParam) {
      return getDefaultChartOptions(customTheme, "No Data Available");
    }

    const timeUnits = filteredDataParam.timeUnits || [];
    const data = filteredDataParam.data || [];
    const isDaily = isDailyParam || filteredDataParam.isDaily || false;

    const validData = data.filter((val) => !isNaN(val) && val !== null && val !== undefined);

    if (timeUnits.length === 0 || validData.length === 0) {
      return getDefaultChartOptions(customTheme, "No Data Available");
    }

    const formattedTimeUnits = timeUnits.map((unit) => {
      if (isDaily) {
        return formatDayForAxis(unit);
      } else {
        return formatMonthForDisplay(unit);
      }
    });

    const isAveragesMode = chartMode.value === "averages";
    const chartTitle = isAveragesMode
      ? `${isDaily ? "Daily" : "Monthly"} Sales Averages`
      : `${isDaily ? "Daily" : "Monthly"} Sales`;

    const averageValue = validData.reduce((sum, val) => sum + val, 0) / validData.length;
    const averageFormatted = formatCurrency(averageValue);

    // Labels are only legible when bars are wide enough (< 32 data points)
    const shouldShowLabels = timeUnits.length < 32;

    const options = {
      backgroundColor: customTheme?.backgroundColor || "#1e1e1e",
      title: {
        text: chartTitle,
        left: "center",
        textStyle: {
          color: customTheme?.textColor || "#ffffff",
          fontSize: 18,
          fontWeight: "bold",
        },
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        backgroundColor: customTheme?.tooltipBackground || "#2a2a2a",
        borderColor: customTheme?.tooltipBorder || "#444444",
        borderWidth: 1,
        textStyle: {
          color: customTheme?.textColor || "#ffffff",
        },
        formatter: function (params) {
          const timeUnit = timeUnits[params[0].dataIndex];
          const displayTime = isDaily
            ? formatDayForDisplay(timeUnit)
            : formatMonthForDisplay(timeUnit);
          const amount = params[0].value;
          const unit = isAveragesMode ? "Average: " : "Sales: ";
          return `${displayTime}<br/>${unit}${formatCurrency(amount)}`;
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "10%",
        top: "15%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: formattedTimeUnits,
        axisLine: {
          lineStyle: {
            color: customTheme?.axisLineColor || "#666666",
          },
        },
        axisLabel: {
          color: customTheme?.textColor || "#ffffff",
          rotate: isDaily ? 45 : 0,
          fontSize: isDaily ? 10 : 12,
        },
        axisTick: {
          alignWithLabel: true,
        },
      },
      yAxis: {
        type: "value",
        name: isAveragesMode ? "Average AED" : "AED",
        nameTextStyle: {
          color: customTheme?.textColor || "#ffffff",
        },
        axisLine: {
          lineStyle: {
            color: customTheme?.axisLineColor || "#666666",
          },
        },
        axisLabel: {
          color: customTheme?.textColor || "#ffffff",
          formatter: function (value) {
            return formatCurrency(value);
          },
        },
        splitLine: {
          lineStyle: {
            color: customTheme?.splitLineColor || "#333333",
            type: "dashed",
          },
        },
      },
      series: [
        {
          name: isAveragesMode ? "Average Sales" : "Sales",
          type: chartType.value,
          data: data,
          itemStyle: {
            color: customTheme?.seriesColor || "#4fc3f7",
          },
          emphasis: {
            itemStyle: {
              color: "#ff9800",
            },
          },
          markLine: {
            silent: true,
            symbol: "none",
            lineStyle: {
              color: "#ff9800",
              type: "dashed",
              width: 1,
            },
            label: {
              formatter: isAveragesMode ? "Avg: {c}" : "Avg: {c}",
              color: "#ffffff",
              backgroundColor: "#ff9800",
              padding: [3, 6],
              borderRadius: 3,
              position: "insideEndTop",
              distance: [10, 0],
            },
            data: [
              {
                name: "Overall Average",
                yAxis: averageValue,
                value: averageValue,
                label: {
                  show: true,
                  formatter: averageFormatted,
                },
              },
            ],
          },
        },
      ],
    };

    if (chartType.value === "bar") {
      if (shouldShowLabels) {
        options.series[0].label = {
          show: true,
          position: "insideTop",
          distance: 15,
          rotate: 0,
          color: "#ffffff",
          fontSize: 11,
          fontWeight: "bold",
          formatter: function (params) {
            const value = params.value;
            // Only show label if bar is at least 10% of average height;
            // very short bars don't have room for a readable label inside
            if (value > 0 && value > averageValue * 0.1) {
              return formatCurrency(value);
            }
            return "";
          },
        };
      } else {
        // Too many bars — rotate x-axis labels instead of showing bar labels
        options.series[0].label = {
          show: false,
        };

        options.xAxis.axisLabel.rotate = 90;
        options.xAxis.axisLabel.fontSize = 9;

        options.grid.bottom = "20%";
      }

      options.series[0].itemStyle = {
        color: customTheme?.seriesColor || "#4fc3f7",
        borderRadius: [4, 4, 0, 0], // Rounded top corners for bars
      };

      options.grid.bottom = shouldShowLabels ? "15%" : "20%";
    }

    if (chartType.value === "line") {
      options.series[0].smooth = true;
      options.series[0].lineStyle = {
        width: 3,
      };
      // Gradient fill under the line makes trend direction easier to read
      options.series[0].areaStyle = {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          {
            offset: 0,
            color: customTheme?.areaGradient?.start || "rgba(79, 195, 247, 0.3)",
          },
          {
            offset: 1,
            color: customTheme?.areaGradient?.end || "rgba(79, 195, 247, 0.1)",
          },
        ]),
      };

      options.series[0].label = {
        show: true,
        position: "top",
        distance: 10,
        color: "#ffffff",
        fontSize: 11,
        fontWeight: "bold",
        formatter: function (params) {
          return formatCurrency(params.value);
        },
      };
    }

    return options;
  };

  const getDefaultChartOptions = (customTheme, titleText = "No Data Available") => {
    return {
      title: {
        text: titleText,
        left: "center",
        top: "center",
        textStyle: {
          color: customTheme?.textColor || "#ffffff",
          fontSize: 16,
        },
      },
      xAxis: { show: false },
      yAxis: { show: false },
      series: [],
    };
  };

  // ── Return public API ────────────────────────────────────────────────────────

  return {
    // Chart properties
    getChartOptions,
    formatCurrency,
    formatAmount,

    // Chart state
    chartData,
    chartLoading,
    chartError,
    chartType,
    isDailyChart,
    showChartMenu,
    chartMode,
    chartExporting,
    darkTheme,

    // Chart computed properties
    hasData,
    filteredData,
    totalSales,
    averagePerTimeUnit,
    bestTimeUnit,
    growthRate,
    totalsSummary,
    timeUnits,

    // Chart actions
    fetchChartData,
    fetchSalesChartData,
    fetchAveragesChartData,
    setChartMode,
    refreshChartData,
    toggleChartType,
    setChartTimeframe,
    resetState,

    // Re-exported filter properties
    filters,
    filterApplied,
    filterCleared,
    isFilterExpanded,
    activeFilters,
    hasActiveFilters,
    filterTotals,

    cityOptions,
    locationOptions,
    brandOptions,
    options,
    datePresets,
    dateRangePreset,

    fetchOptions,
    applyFilters,
    clearFilters,
    setFilter,
    setFilters,
    applyDatePreset,
    shallowEqual,
    deepEqual,
    toggleFilterExpand,

    getCitiesWithData,
    getLocationsWithData,
    getBrandsWithData,
    getPlatformsWithData,
    getLocationCityTotal,
    getBrandLocationCityTotal,
    getPlatformTotalForBrand,
    getLocationCityDayTotal,
    getBrandLocationCityDayTotal,
    getPlatformData,
    getCityTotal,
    getLocationTotal,
    getBrandTotal,
    getCityAverage,
    getLocationCityAverage,
    getBrandLocationCityAverage,
    getPlatformAverageForBrand,
    getGrandAverage,

    expandedCities,
    expandedLocations,
    expandedBrands,
    isCityExpanded,
    isLocationExpanded,
    isBrandExpanded,
    toggleCity,
    toggleLocation,
    toggleBrand,
    expandAll,
    collapseAll,

    filterSummary,
    numberOfActiveFilters,

    // Utility functions
    safeArray,
    getSafeValue,
    formatMonthForDisplay,
    formatDayForDisplay,
    formatDayForAxis,
    formatTimeUnitForDisplay,
  };
}
